import { useMap } from "@vis.gl/react-maplibre";
import { useEffect } from "react";
import useEvent from "react-use-event-hook";

import { getAliveMap } from "./maplibre-map.utils";
import { MAPLIBRE_PLACE_INFO_STATUS } from "./maplibre-place-info.constants";
import { formatPlaceInfoCoordinate } from "./maplibre-place-info.utils";
import { useMapLibrePlaceInfoClicks } from "./use-maplibre-place-info-clicks";
import { useMapLibrePlaceInfoLookup } from "./use-maplibre-place-info-lookup";

/** The place-info mode of one map: the toggle, the picked point's lookup and Esc. */
export const useMapLibrePlaceInfo = ({
  isActive,
  onActiveChange,
}: {
  isActive: boolean;
  onActiveChange: (isActive: boolean) => void;
}) => {
  const { current: mapRef } = useMap();
  const { lookup, lookUp, retry, close } = useMapLibrePlaceInfoLookup();
  // useEvent callbacks read primitives through a container so they always see the current values.
  const mode = { isActive };

  useMapLibrePlaceInfoClicks({ isActive, onPick: lookUp });

  // The mode can also end from outside — an editing tool picked meanwhile — and the popup goes with it.
  useEffect(() => {
    if (!isActive) {
      close();
    }
  }, [close, isActive]);

  const toggle = useEvent((): void => {
    if (mode.isActive) {
      close();
    }

    onActiveChange(!mode.isActive);
  });

  // A marker click reaches the map beneath it; the dot only marks the point, so its click does nothing.
  const handlePointClick = useEvent(
    (event: { originalEvent: MouseEvent }): void =>
      event.originalEvent.stopPropagation(),
  );

  /*
   * MapLibre focuses the popup's own button when it opens, so closing it would drop focus to the page —
   * where the map's keys, the second Esc included, no longer arrive. Focus goes back to the map instead.
   */
  const closePopup = useEvent((): void => {
    close();
    getAliveMap(mapRef)?.getCanvas().focus({ preventScroll: true });
  });

  /*
   * Esc takes one step back: the open popup first, then the mode itself. It is consumed, so a paused lasso
   * does not cancel on it and a drawer around the map does not close on it too.
   */
  const handleEscape = useEvent((event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    if (lookup) {
      closePopup();
      return;
    }

    onActiveChange(false);
  });

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !isActive) {
      return undefined;
    }

    const container = map.getContainer();
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleEscape(event);
      }
    };

    container.addEventListener("keydown", handleKeyDown);

    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [handleEscape, isActive, mapRef]);

  return {
    lookup,
    // A new point remounts the popup, so it pans into view for each click, not only the first.
    popupKey: lookup ? formatPlaceInfoCoordinate(lookup.coordinate) : "",
    isLoading: lookup?.status === MAPLIBRE_PLACE_INFO_STATUS.Loading,
    isHintShown: isActive && !lookup,
    handlePointClick,
    toggle,
    retry,
    closePopup,
  };
};
